import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { getUser } from '@frontend/atoms/opalUserAtom';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';

import { sendSentryError } from '../utils/sendSentryError';

const MAX_RETRIES = 2;

type RetryableAxiosRequestConfig = {
  _retryCount?: number;
} & InternalAxiosRequestConfig;

class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL?: string, withAuth: boolean = false) {
    this.axiosInstance = axios.create({
      baseURL,
      withCredentials: true,
      timeout: 10000,
    });

    this.setupInterceptors(withAuth);
  }

  private setupInterceptors(withAuth: boolean): void {
    if (withAuth) {
      this.axiosInstance.interceptors.request.use(async config => {
        config.headers = config.headers || {};
        const token = await getOrRenewOptiIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
    }

    this.axiosInstance.interceptors.request.use(config => {
      config.headers = config.headers || {};
      const user = getUser();
      if (user?.instance?.instance_id) {
        config.headers['X-Instance-Id'] = user.instance.instance_id;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const config = error.config as RetryableAxiosRequestConfig | undefined;
        const { response } = error;
        const status = response?.status;

        // Only retry on 429 (Too Many Requests), not on 401, 403, or other 4xx errors
        if (config && status === 429 && (!config._retryCount || config._retryCount < MAX_RETRIES)) {
          config._retryCount = config._retryCount ? config._retryCount + 1 : 1;
          return this.axiosInstance(config);
        }

        const errorContext = {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        };
        sendSentryError(error, errorContext);
        return Promise.reject(error);
      }
    );
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Extracts a meaningful error message from API responses, particularly AxiosError instances.
 *
 * @param error - The error object to extract a message from
 * @returns A human-readable error message
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/endpoint', data);
 * } catch (error) {
 *   const message = extractApiErrorMessage(error);
 *   throw new Error(message);
 * }
 * ```
 */
export function extractApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    // Check for detailed message in response.data.detail.message
    const detailMessage = error.response?.data?.detail?.message;
    if (detailMessage && typeof detailMessage === 'string') {
      return detailMessage;
    }

    // Fallback to response.data.detail if it's a string
    const detail = error.response?.data?.detail;
    if (detail && typeof detail === 'string') {
      return detail;
    }

    // Fallback to response.data.message
    const message = error.response?.data?.message;
    if (message && typeof message === 'string') {
      return message;
    }
  }

  // Generic fallback
  return 'An error occurred while processing your request';
}

const authenticatedHttpClient = new HttpClient(undefined, true);
export default authenticatedHttpClient;
